import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle } from "@untitledui/icons";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { Button } from "@/components/base/buttons/button";

/**
 * hero-maritime.jpg — Nave portacontainer in oceano (trasporto via mare).
 * Fotografo: Thomas Marquize — https://unsplash.com/@tomarquize
 * Pagina foto: https://unsplash.com/photos/a-container-ship-sails-alone-in-the-ocean-NA1ESlGA3Lw
 * Licenza: https://unsplash.com/license
 *
 * hero-sea-mask.jpg — Onde del mare (texture per text clipping mask).
 * Fotografo: Jeremy Bishop — https://unsplash.com/@jeremybishop
 * Pagina foto: https://unsplash.com/photos/7KLa-xLbSXA
 * Licenza: https://unsplash.com/license
 */
const HERO_IMAGE_SRC = "/images/marketing/hero-maritime.jpg";
const HERO_SEA_MASK_SRC = "/images/marketing/hero-sea-mask.jpg";

type TypewriterPhase = "typing" | "pauseFull" | "deleting" | "pauseEmpty";

function asStringArray(value: unknown): string[] {
    return Array.isArray(value) && value.every((x) => typeof x === "string") ? (value as string[]) : [];
}

export function LandingHero() {
    const { t, i18n } = useTranslation("pages:landing");
    const shouldReduceMotion = useReducedMotion();
    const typewriterPrefix = t("hero.typewriterPrefix");
    const typewriterSuffixes = useMemo(
        () => asStringArray(t("hero.typewriterSuffixes", { returnObjects: true })),
        [t, i18n.language],
    );
    const fallbackLine2 = t("hero.titleLine2");
    const fallbackSuffix = fallbackLine2.startsWith(`${typewriterPrefix} `)
        ? fallbackLine2.slice(typewriterPrefix.length + 1)
        : fallbackLine2;
    const suffixes = typewriterSuffixes.length > 0 ? typewriterSuffixes : [fallbackSuffix];
    const [phraseIndex, setPhraseIndex] = useState(0);
    const [typedCount, setTypedCount] = useState(0);
    const [phase, setPhase] = useState<TypewriterPhase>("typing");

    // scrollY globale: a scroll 0 il parallasse è 0. Con target+["start end","end start"]
    // il progress può essere > 0 con l'hero già in vista e translateY sull'img crea una striscia sopra.
    const { scrollY } = useScroll();
    const imageY = useTransform(scrollY, [0, 520], [0, shouldReduceMotion ? 0 : 34]);
    const imageScale = useTransform(scrollY, [0, 520], [1, shouldReduceMotion ? 1 : 1.03]);
    const overlayOpacity = 0.7;
    const activeSuffix = suffixes[phraseIndex % suffixes.length] ?? "";

    useEffect(() => {
        if (shouldReduceMotion) return;

        let delay = 0;
        let onTick: () => void = () => { };
        if (phase === "typing") {
            if (typedCount < activeSuffix.length) {
                delay = 60 + Math.floor(Math.random() * 21);
                onTick = () => setTypedCount((v) => Math.min(v + 1, activeSuffix.length));
            } else {
                delay = 1800;
                onTick = () => setPhase("pauseFull");
            }
        } else if (phase === "pauseFull") {
            delay = 420;
            onTick = () => setPhase("deleting");
        } else if (phase === "deleting") {
            if (typedCount > 0) {
                delay = 44 + Math.floor(Math.random() * 13);
                onTick = () => setTypedCount((v) => Math.max(v - 1, 0));
            } else {
                delay = 360;
                onTick = () => setPhase("pauseEmpty");
            }
        } else {
            delay = 220;
            onTick = () => {
                setPhraseIndex((v) => (v + 1) % suffixes.length);
                setPhase("typing");
            };
        }

        const timeoutId = window.setTimeout(onTick, delay);
        return () => window.clearTimeout(timeoutId);
    }, [activeSuffix.length, phase, shouldReduceMotion, suffixes.length, typedCount]);

    const typedSuffix = shouldReduceMotion ? fallbackSuffix : activeSuffix.slice(0, typedCount);

    return (
        <section className="relative flex min-h-[min(80svh,46rem)] flex-col overflow-hidden py-8 sm:min-h-[min(86svh,52rem)] sm:py-14 lg:py-16">
            {/* Full-bleed background (Raft-style): foto + overlay lettura */}
            <div className="absolute inset-0 z-0" aria-hidden>
                <motion.div className="absolute inset-0 h-full min-h-full w-full" style={{ y: imageY, scale: imageScale }}>
                    <img
                        src={HERO_IMAGE_SRC}
                        alt=""
                        width={1920}
                        height={1080}
                        loading="eager"
                        decoding="async"
                        className="h-full min-h-full w-full object-cover object-[center_52%]"
                    />
                </motion.div>
                <motion.div style={{ opacity: overlayOpacity }}>
                    {/* Scuro a sinistra per il copy; dissolve verso destra */}
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/92 to-secondary/35 sm:via-secondary/80 sm:to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/22 to-secondary/45" />
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-500/10 via-transparent to-indigo-500/15" />
                </motion.div>
            </div>

            <motion.div
                aria-hidden
                className="pointer-events-none absolute -left-20 top-16 z-[1] h-64 w-64 rounded-full bg-brand-500/10 blur-3xl"
                animate={shouldReduceMotion ? undefined : { x: [0, 20, 0], y: [0, -16, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                aria-hidden
                className="pointer-events-none absolute right-0 top-1/3 z-[1] h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl"
                animate={shouldReduceMotion ? undefined : { x: [0, -14, 0], y: [0, 10, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative z-[2] mx-auto flex w-full max-w-7xl flex-1 items-start px-4 pt-6 sm:px-6 sm:pt-10 lg:px-8 lg:pt-12">
                <motion.div
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55 }}
                    className="max-w-xl space-y-5 sm:max-w-3xl sm:space-y-6 lg:max-w-[54rem] xl:max-w-[60rem]"
                >
                    <p className="inline-flex items-center gap-2 rounded-full border border-brand-500/70 bg-brand-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                        <CheckCircle className="size-4" />
                        {t("hero.badge")}
                    </p>

                    <div className="space-y-1.5">
                        <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight text-primary sm:text-5xl sm:leading-[1.02] lg:text-6xl xl:text-[3.5rem]">
                            <span className="block drop-shadow-sm">{t("hero.titleLine1")}</span>
                            <span
                                className="mt-1 block bg-clip-text text-transparent drop-shadow-sm sm:whitespace-nowrap"
                                style={{
                                    backgroundImage: `url(${HERO_SEA_MASK_SRC})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                }}
                            >
                                {typewriterPrefix} {typedSuffix}
                                {!shouldReduceMotion ? (
                                    <span
                                        className="ml-1 inline-block align-baseline text-primary"
                                        style={{ textShadow: "0 0 18px rgb(255 255 255 / 0.35)" }}
                                    >
                                        |
                                    </span>
                                ) : null}
                            </span>
                        </h1>
                    </div>

                    <p className="max-w-xl text-base leading-relaxed text-quaternary sm:text-lg">{t("hero.subtitle")}</p>

                    <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <Button href="/auth/login" size="xl" color="primary" iconTrailing={ArrowRight} className="w-full sm:w-auto">
                            {t("hero.ctaPrimary")}
                        </Button>
                        <Button href="#features" size="xl" color="secondary" className="w-full sm:w-auto">
                            {t("hero.ctaExplore")}
                        </Button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
